const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const n = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);
const rp = (x, y) => ({ x: Number(x.toFixed(4)), y: Number(y.toFixed(4)) });
const toSVG = (point) => `${point.x} ${point.y}`;

const normalize = (vector, fallback = { x: 1, y: 0 }) => {
  const mag = Math.hypot(vector.x, vector.y);
  if (mag < 1e-8) return fallback;
  return { x: vector.x / mag, y: vector.y / mag };
};

const rotatePoint = (point, origin, cos, sin) => ({
  x: origin.x + ((point.x - origin.x) * cos) - ((point.y - origin.y) * sin),
  y: origin.y + ((point.x - origin.x) * sin) + ((point.y - origin.y) * cos),
});

const quadPoint = (p0, p1, p2, t) => {
  const inv = 1 - t;
  return {
    x: (inv * inv * p0.x) + (2 * inv * t * p1.x) + (t * t * p2.x),
    y: (inv * inv * p0.y) + (2 * inv * t * p1.y) + (t * t * p2.y),
  };
};

const quadTangent = (p0, p1, p2, t, fallback = { x: -1, y: 0 }) => normalize({
  x: (2 * (1 - t) * (p1.x - p0.x)) + (2 * t * (p2.x - p1.x)),
  y: (2 * (1 - t) * (p1.y - p0.y)) + (2 * t * (p2.y - p1.y)),
}, fallback);

const quadLength = (p0, p1, p2, steps = 56) => {
  let total = 0;
  let prev = p0;
  for (let index = 1; index <= steps; index += 1) {
    const point = quadPoint(p0, p1, p2, index / steps);
    total += dist(prev, point);
    prev = point;
  }
  return total;
};

const quadSplitAtT = (p0, p1, p2, tRaw) => {
  const t = clamp(tRaw, 0, 1);
  const a = {
    x: p0.x + ((p1.x - p0.x) * t),
    y: p0.y + ((p1.y - p0.y) * t),
  };
  const b = {
    x: p1.x + ((p2.x - p1.x) * t),
    y: p1.y + ((p2.y - p1.y) * t),
  };
  const m = {
    x: a.x + ((b.x - a.x) * t),
    y: a.y + ((b.y - a.y) * t),
  };
  return {
    splitPoint: m,
    left: { p0, p1: a, p2: m },
    right: { p0: m, p1: b, p2 },
  };
};

const solveQuadTForArcDistanceFromStart = (p0, p1, p2, targetDistance) => {
  const total = quadLength(p0, p1, p2, 56);
  const clamped = clamp(targetDistance, 0, total);
  if (clamped <= 1e-6) return 0;
  if (Math.abs(clamped - total) <= 1e-6) return 1;

  let lo = 0;
  let hi = 1;
  for (let index = 0; index < 28; index += 1) {
    const mid = (lo + hi) / 2;
    const split = quadSplitAtT(p0, p1, p2, mid);
    const len = quadLength(split.left.p0, split.left.p1, split.left.p2, 42);
    if (len < clamped) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
};

const sampleQuadratic = (p0, p1, p2, steps = 36) => (
  Array.from({ length: steps + 1 }, (_, index) => quadPoint(p0, p1, p2, index / steps))
);

const polylineLength = (points = []) => points.slice(1).reduce(
  (sum, point, index) => sum + dist(points[index], point),
  0,
);

const polylineToPath = (points = []) => {
  if (!points.length) return '';
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${toSVG(point)}`).join(' ');
};

const pointAlongPolyline = (points = [], targetDistance = 0) => {
  if (!points.length) return rp(0, 0);
  if (points.length === 1) return rp(points[0].x, points[0].y);
  const total = polylineLength(points);
  const clamped = clamp(targetDistance, 0, total);
  let walked = 0;
  for (let index = 1; index < points.length; index += 1) {
    const prev = points[index - 1];
    const curr = points[index];
    const seg = dist(prev, curr);
    if (walked + seg >= clamped) {
      const t = seg > 1e-9 ? (clamped - walked) / seg : 0;
      return rp(prev.x + ((curr.x - prev.x) * t), prev.y + ((curr.y - prev.y) * t));
    }
    walked += seg;
  }
  return rp(points[points.length - 1].x, points[points.length - 1].y);
};

const offsetAlongNormal = (point, tangent, distance, prefer) => {
  const t = normalize(tangent);
  let normal = { x: -t.y, y: t.x };
  if (prefer && ((normal.x * prefer.x) + (normal.y * prefer.y) < 0)) {
    normal = { x: -normal.x, y: -normal.y };
  }
  return rp(point.x + (normal.x * distance), point.y + (normal.y * distance));
};

const samplePolylineTangents = (points = []) => points.map((point, index) => {
  const prev = points[Math.max(0, index - 1)];
  const next = points[Math.min(points.length - 1, index + 1)];
  return {
    point,
    tangent: normalize({ x: next.x - prev.x, y: next.y - prev.y }),
  };
});

const cubicControlsFromPolyline = (points = []) => {
  const p0 = points[0];
  const p3 = points[points.length - 1];
  if (!p0 || !p3) return { startCtrl: p0, endCtrl: p3 };
  const total = Math.max(1e-4, polylineLength(points));
  const q13 = pointAlongPolyline(points, total / 3);
  const q23 = pointAlongPolyline(points, (total * 2) / 3);
  const t = 1 / 3;
  const s = 2 / 3;
  const a1 = 3 * ((1 - t) ** 2) * t;
  const a2 = 3 * (1 - t) * (t ** 2);
  const b1 = 3 * ((1 - s) ** 2) * s;
  const b2 = 3 * (1 - s) * (s ** 2);
  const r1 = {
    x: q13.x - (((1 - t) ** 3) * p0.x) - ((t ** 3) * p3.x),
    y: q13.y - (((1 - t) ** 3) * p0.y) - ((t ** 3) * p3.y),
  };
  const r2 = {
    x: q23.x - (((1 - s) ** 3) * p0.x) - ((s ** 3) * p3.x),
    y: q23.y - (((1 - s) ** 3) * p0.y) - ((s ** 3) * p3.y),
  };
  const det = (a1 * b2) - (a2 * b1);
  if (Math.abs(det) < 1e-8) {
    return {
      startCtrl: rp((p0.x * 2 + p3.x) / 3, (p0.y * 2 + p3.y) / 3),
      endCtrl: rp((p0.x + p3.x * 2) / 3, (p0.y + p3.y * 2) / 3),
    };
  }
  return {
    startCtrl: rp(((r1.x * b2) - (a2 * r2.x)) / det, ((r1.y * b2) - (a2 * r2.y)) / det),
    endCtrl: rp(((a1 * r2.x) - (r1.x * b1)) / det, ((a1 * r2.y) - (r1.y * b1)) / det),
  };
};

const LENGTH_TOLERANCE = 0.0625;

const fitQuadControl = (p0, p2, samples = []) => {
  const mid = samples.length
    ? pointAlongPolyline(samples, polylineLength(samples) / 2)
    : rp((p0.x + p2.x) / 2, (p0.y + p2.y) / 2);
  return rp((2 * mid.x) - (0.5 * p0.x) - (0.5 * p2.x), (2 * mid.y) - (0.5 * p0.y) - (0.5 * p2.y));
};

const cubicPoint = (p0, p1, p2, p3, t) => {
  const inv = 1 - t;
  return {
    x: (inv ** 3 * p0.x) + (3 * inv * inv * t * p1.x) + (3 * inv * t * t * p2.x) + (t ** 3 * p3.x),
    y: (inv ** 3 * p0.y) + (3 * inv * inv * t * p1.y) + (3 * inv * t * t * p2.y) + (t ** 3 * p3.y),
  };
};

const intersectRayWithCubic = (origin, toward, p0, p1, p2, p3, steps = 72) => {
  const dir = normalize({ x: toward.x - origin.x, y: toward.y - origin.y });
  const sideOf = (point) => ((point.x - origin.x) * -dir.y) + ((point.y - origin.y) * dir.x);
  let prev = cubicPoint(p0, p1, p2, p3, 0);
  let prevSide = sideOf(prev);
  let best = null;
  let bestScore = Infinity;
  for (let index = 1; index <= steps; index += 1) {
    const curr = cubicPoint(p0, p1, p2, p3, index / steps);
    const currSide = sideOf(curr);
    if (prevSide * currSide <= 0) {
      const denom = Math.abs(prevSide) + Math.abs(currSide);
      const u = denom > 1e-9 ? Math.abs(prevSide) / denom : 0;
      const hit = {
        x: prev.x + ((curr.x - prev.x) * u),
        y: prev.y + ((curr.y - prev.y) * u),
      };
      const along = ((hit.x - origin.x) * dir.x) + ((hit.y - origin.y) * dir.y);
      if (along > 0.05) {
        const score = dist(hit, toward);
        if (score < bestScore) {
          bestScore = score;
          best = hit;
        }
      }
    }
    prev = curr;
    prevSide = currSide;
  }
  return best ? rp(best.x, best.y) : rp(toward.x, toward.y);
};

const BUTTONHOLE_HALF = 0.3125;

const buildStandHardware = (start, end, id, piece, centerRaw) => {
  const center = centerRaw || rp((start.x + end.x) / 2, (start.y + end.y) / 2);
  const stand = normalize({ x: end.x - start.x, y: end.y - start.y });
  let across = { x: -stand.y, y: stand.x };
  if (across.x < 0) across = { x: -across.x, y: -across.y };
  return {
    button: { id, piece, x: center.x, y: center.y },
    hole: {
      id,
      piece,
      orientation: 'across',
      start: rp(center.x - (across.x * BUTTONHOLE_HALF), center.y - (across.y * BUTTONHOLE_HALF)),
      end: rp(center.x + (across.x * BUTTONHOLE_HALF), center.y + (across.y * BUTTONHOLE_HALF)),
    },
  };
};

const sampleCubic = (p0, p1, p2, p3, steps = 36) => (
  Array.from({ length: steps + 1 }, (_, index) => cubicPoint(p0, p1, p2, p3, index / steps))
);

const closestCubicDistance = (p0, p1, p2, p3, target, steps = 40) => {
  let best = dist(p0, target);
  for (let index = 1; index <= steps; index += 1) {
    best = Math.min(best, dist(cubicPoint(p0, p1, p2, p3, index / steps), target));
  }
  return best;
};

const lerpPoint = (a, b, t) => ({
  x: a.x + ((b.x - a.x) * t),
  y: a.y + ((b.y - a.y) * t),
});

const standCenterEquidistant = (f0, f1, bottom, top) => {
  const errorAt = (t) => {
    const point = lerpPoint(f0, f1, t);
    return Math.abs(
      closestCubicDistance(bottom.p0, bottom.p1, bottom.p2, bottom.p3, point, 36)
      - closestCubicDistance(top.p0, top.p1, top.p2, top.p3, point, 36),
    );
  };
  let bestT = 0.5;
  let bestErr = Infinity;
  for (let index = 0; index <= 32; index += 1) {
    const t = index / 32;
    const error = errorAt(t);
    if (error < bestErr) {
      bestErr = error;
      bestT = t;
    }
  }
  let lo = Math.max(0, bestT - (1 / 32));
  let hi = Math.min(1, bestT + (1 / 32));
  for (let index = 0; index < 10; index += 1) {
    const left = lo + ((hi - lo) / 3);
    const right = hi - ((hi - lo) / 3);
    if (errorAt(left) < errorAt(right)) hi = right;
    else lo = left;
  }
  const center = lerpPoint(f0, f1, (lo + hi) / 2);
  return rp(center.x, center.y);
};

const cubicLengthNear = (p0, p1, p2, p3, target, steps = 72) => {
  let walked = 0;
  let bestWalked = 0;
  let bestDist = dist(p0, target);
  let prev = p0;
  for (let index = 1; index <= steps; index += 1) {
    const point = cubicPoint(p0, p1, p2, p3, index / steps);
    walked += dist(prev, point);
    const currentDist = dist(point, target);
    if (currentDist < bestDist) {
      bestDist = currentDist;
      bestWalked = walked;
    }
    prev = point;
  }
  return bestWalked;
};

const hasManualDelta = (...values) => values.some((value) => Math.abs(n(value, 0)) > 1e-8);

export function expandDraftViewBox(viewBox, draftPoints = [], pad = 2.5) {
  if (!viewBox || !draftPoints.length) return viewBox;
  const xs = draftPoints.map((point) => point.x);
  const ys = draftPoints.map((point) => point.y);
  const minX = Math.min(viewBox.x, Math.min(...xs) - pad);
  const maxX = Math.max(viewBox.x + viewBox.width, Math.max(...xs) + pad);
  const minY = Math.min(viewBox.y, Math.min(...ys) - pad);
  const maxY = Math.max(viewBox.y + viewBox.height, Math.max(...ys) + pad);
  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function buildShirtCollarBand({
  CF1,
  FHPS,
  frontCtrl,
  E1,
  halfNeckLength,
  frontNeckLength,
  buttonExtensionWidth,
  frontNeckTracePercent = 30,
  bandBackHeight = 1.25,
  bandFrontHeight = 1,
  topStartCtrlDx = 0,
  topStartCtrlDy = 0,
  topEndCtrlDx = 0,
  topEndCtrlDy = 0,
  topFollowBottom = true,
  topStartAbsX = 0,
  topStartAbsY = 0,
  topEndAbsX = 0,
  topEndAbsY = 0,
  bottomStartCtrlDx = 0,
  bottomStartCtrlDy = 0,
  bottomEndCtrlDx = 0,
  bottomEndCtrlDy = 0,
  neckCtrlDx = 0,
  neckCtrlDy = 0,
  layoutB0Dy = 0,
  layoutB1Dy = 0,
  layoutOrigin,
} = {}) {
  if (!CF1 || !FHPS || !frontCtrl) return null;

  const targetHalfNeck = Math.max(0.5, n(halfNeckLength, dist(CF1, FHPS)));
  const frontLen = Math.max(1e-4, n(frontNeckLength, quadLength(CF1, frontCtrl, FHPS)));
  const tracePercent = clamp(n(frontNeckTracePercent, 30), 5, 95) / 100;
  const walkedTarget = Math.min(frontLen * tracePercent, targetHalfNeck);
  const walkT = solveQuadTForArcDistanceFromStart(CF1, frontCtrl, FHPS, walkedTarget);
  const split = quadSplitAtT(CF1, frontCtrl, FHPS, walkT);
  const PTrace = split.splitPoint;
  const remaining = Math.max(0, targetHalfNeck - walkedTarget);
  const continueTangent = quadTangent(CF1, frontCtrl, FHPS, walkT, normalize({
    x: FHPS.x - CF1.x,
    y: FHPS.y - CF1.y,
  }));
  const startTangent = quadTangent(CF1, frontCtrl, FHPS, 0, continueTangent);
  const extensionWidth = Math.max(0, n(buttonExtensionWidth, 0.625));

  const CbF0 = rp(CF1.x, CF1.y);
  const CbB0 = rp(
    PTrace.x + (continueTangent.x * remaining),
    PTrace.y + (continueTangent.y * remaining),
  );
  const CbE0 = E1
    ? rp(E1.x, E1.y)
    : rp(
      CbF0.x - (startTangent.x * extensionWidth),
      CbF0.y - (startTangent.y * extensionWidth),
    );

  const standPrefer = { x: 0, y: -1 };
  const frontHeight = Math.max(0.25, n(bandFrontHeight, 1));
  const backHeight = Math.max(0.25, n(bandBackHeight, 1.25));
  const neckFollowCtrl = split.left.p1;
  const resolvedBottomStartDx = n(bottomStartCtrlDx, 0) || n(neckCtrlDx, 0);
  const resolvedBottomStartDy = n(bottomStartCtrlDy, 0) || n(neckCtrlDy, 0);
  const resolvedBottomEndDx = n(bottomEndCtrlDx, 0);
  const resolvedBottomEndDy = n(bottomEndCtrlDy, 0);
  const buildNeckPolyline = (backPoint) => {
    const remainCtrl = fitQuadControl(backPoint, PTrace, [backPoint, PTrace]);
    const remainSamples = sampleQuadratic(backPoint, remainCtrl, PTrace, 12);
    const followSamples = sampleQuadratic(PTrace, neckFollowCtrl, CbF0, 20).slice(1);
    return [...remainSamples, ...followSamples];
  };
  const controlsFromNeck = (backPoint) => {
    const fitted = cubicControlsFromPolyline(buildNeckPolyline(backPoint));
    return {
      startCtrlBase: fitted.startCtrl,
      endCtrlBase: fitted.endCtrl,
      startCtrl: rp(fitted.startCtrl.x + resolvedBottomStartDx, fitted.startCtrl.y + resolvedBottomStartDy),
      endCtrl: rp(fitted.endCtrl.x + resolvedBottomEndDx, fitted.endCtrl.y + resolvedBottomEndDy),
    };
  };

  const constructionB1 = offsetAlongNormal(CbB0, continueTangent, backHeight, standPrefer);
  const standSeedF1 = offsetAlongNormal(CbF0, startTangent, frontHeight, standPrefer);
  const constructionE1 = offsetAlongNormal(
    CbE0,
    { x: CbE0.x - CbF0.x, y: CbE0.y - CbF0.y },
    frontHeight,
    standPrefer,
  );

  const backEdge = { x: constructionB1.x - CbB0.x, y: constructionB1.y - CbB0.y };
  const rotation = (-Math.PI / 2) - Math.atan2(backEdge.y, backEdge.x);
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);
  const target = layoutOrigin || {
    x: 1,
    y: Math.min(CbF0.y, FHPS.y) - backHeight - 2.75,
  };
  const mapToLayout = (point) => {
    const rotated = rotatePoint(point, CbB0, cos, sin);
    return rp(rotated.x + (target.x - CbB0.x), rotated.y + (target.y - CbB0.y));
  };
  const mapFromLayout = (point) => {
    const shifted = { x: point.x - (target.x - CbB0.x), y: point.y - (target.y - CbB0.y) };
    const body = rotatePoint(shifted, CbB0, cos, -sin);
    return rp(body.x, body.y);
  };

  const mappedB0 = mapToLayout(CbB0);
  const mappedConstructionB1 = mapToLayout(constructionB1);
  const upSign = mappedConstructionB1.y >= mappedB0.y ? 1 : -1;
  let layoutB0 = rp(mappedB0.x, mappedB0.y + n(layoutB0Dy, 0));
  let layoutB1 = rp(layoutB0.x, layoutB0.y + (upSign * backHeight));

  const evaluateSlide = (slideX) => {
    const backPoint = mapFromLayout(rp(layoutB0.x + slideX, layoutB0.y));
    const neck = controlsFromNeck(backPoint);
    return polylineLength(sampleCubic(backPoint, neck.startCtrl, neck.endCtrl, CbF0, 56));
  };

  let slideX = 0;
  let neckLength = evaluateSlide(0);
  if (Math.abs(neckLength - targetHalfNeck) > LENGTH_TOLERANCE) {
    const awayFromFront = neckLength < targetHalfNeck ? -1 : 1;
    let lo = 0;
    let hi = awayFromFront * 0.25;
    let hiLen = evaluateSlide(hi);
    while (Math.abs(hi) < 4 && (neckLength - targetHalfNeck) * (hiLen - targetHalfNeck) > 0) {
      hi *= 2;
      hiLen = evaluateSlide(hi);
    }
    for (let index = 0; index < 28; index += 1) {
      const mid = (lo + hi) / 2;
      const midLen = evaluateSlide(mid);
      if (Math.abs(midLen - targetHalfNeck) <= LENGTH_TOLERANCE) {
        slideX = mid;
        neckLength = midLen;
        break;
      }
      if ((midLen - targetHalfNeck) * (evaluateSlide(lo) - targetHalfNeck) <= 0) {
        hi = mid;
      } else {
        lo = mid;
      }
      slideX = mid;
      neckLength = midLen;
    }
  }

  layoutB0 = rp(layoutB0.x + slideX, layoutB0.y);
  layoutB1 = rp(layoutB1.x + slideX, layoutB1.y);
  const bodyB0 = mapFromLayout(layoutB0);
  const bodyB1 = mapFromLayout(layoutB1);
  const CbE1 = constructionE1;

  const bottom = controlsFromNeck(bodyB0);
  const bottomStartCtrl = bottom.startCtrl;
  const bottomEndCtrl = bottom.endCtrl;
  const neckDrawn = sampleCubic(bodyB0, bottomStartCtrl, bottomEndCtrl, CbF0, 28);
  const drawnBottom = [...neckDrawn, CbE0];
  const neckSpan = Math.max(1e-4, polylineLength(neckDrawn));
  let walked = 0;
  const liveTopSamples = samplePolylineTangents(drawnBottom).map((sample, index) => {
    if (index > 0) walked += dist(drawnBottom[index - 1], sample.point);
    const height = walked <= neckSpan
      ? backHeight + ((frontHeight - backHeight) * clamp(walked / neckSpan, 0, 1))
      : frontHeight;
    return offsetAlongNormal(sample.point, sample.tangent, height, standPrefer);
  });
  liveTopSamples[0] = bodyB1;
  const liveTopFit = cubicControlsFromPolyline(liveTopSamples);
  const topFollows = topFollowBottom !== false && topFollowBottom !== 0 && topFollowBottom !== '0';
  const hasFrozenTop = hasManualDelta(topStartAbsX, topStartAbsY) && hasManualDelta(topEndAbsX, topEndAbsY);
  const topStartCtrlBase = (!topFollows && hasFrozenTop)
    ? rp(n(topStartAbsX), n(topStartAbsY))
    : liveTopFit.startCtrl;
  const topEndCtrlBase = (!topFollows && hasFrozenTop)
    ? rp(n(topEndAbsX), n(topEndAbsY))
    : liveTopFit.endCtrl;
  const topStartCtrl = rp(topStartCtrlBase.x + n(topStartCtrlDx, 0), topStartCtrlBase.y + n(topStartCtrlDy, 0));
  const topEndCtrl = rp(topEndCtrlBase.x + n(topEndCtrlDx, 0), topEndCtrlBase.y + n(topEndCtrlDy, 0));
  const CbF1 = intersectRayWithCubic(CbF0, standSeedF1, bodyB1, topStartCtrl, topEndCtrl, CbE1);

  const path = [
    `M ${toSVG(bodyB0)}`,
    `C ${toSVG(bottomStartCtrl)} ${toSVG(bottomEndCtrl)} ${toSVG(CbF0)}`,
    `L ${toSVG(CbE0)}`,
    `L ${toSVG(CbE1)}`,
    `C ${toSVG(topEndCtrl)} ${toSVG(topStartCtrl)} ${toSVG(bodyB1)}`,
    'Z',
  ].join(' ');

  const layoutPoints = {
    CbB0: layoutB0,
    CbB1: layoutB1,
    CbT0: mapToLayout(PTrace),
    CbF0: mapToLayout(CbF0),
    CbF1: mapToLayout(CbF1),
    CbE0: mapToLayout(CbE0),
    CbE1: mapToLayout(CbE1),
  };
  const layoutBottomStartCtrl = mapToLayout(bottomStartCtrl);
  const layoutBottomEndCtrl = mapToLayout(bottomEndCtrl);
  const layoutStartCtrl = mapToLayout(topStartCtrl);
  const layoutEndCtrl = mapToLayout(topEndCtrl);
  const layoutPath = [
    `M ${toSVG(layoutB0)}`,
    `C ${toSVG(layoutBottomStartCtrl)} ${toSVG(layoutBottomEndCtrl)} ${toSVG(layoutPoints.CbF0)}`,
    `L ${toSVG(layoutPoints.CbE0)}`,
    `L ${toSVG(layoutPoints.CbE1)}`,
    `C ${toSVG(layoutEndCtrl)} ${toSVG(layoutStartCtrl)} ${toSVG(layoutB1)}`,
    'Z',
  ].join(' ');

  const bodyHardware = buildStandHardware(
    CbF0,
    CbF1,
    'collar',
    'collar',
    standCenterEquidistant(
      CbF0,
      CbF1,
      { p0: bodyB0, p1: bottomStartCtrl, p2: bottomEndCtrl, p3: CbF0 },
      { p0: bodyB1, p1: topStartCtrl, p2: topEndCtrl, p3: CbE1 },
    ),
  );
  const layoutHardware = buildStandHardware(
    layoutPoints.CbF0,
    layoutPoints.CbF1,
    'collar-layout',
    'collarLayout',
    standCenterEquidistant(
      layoutPoints.CbF0,
      layoutPoints.CbF1,
      { p0: layoutB0, p1: layoutBottomStartCtrl, p2: layoutBottomEndCtrl, p3: layoutPoints.CbF0 },
      { p0: layoutB1, p1: layoutStartCtrl, p2: layoutEndCtrl, p3: layoutPoints.CbE1 },
    ),
  );

  return {
    path,
    layoutPath,
    layoutTransform: {
      cos,
      sin,
      shiftX: target.x - CbB0.x,
      shiftY: target.y - CbB0.y,
      pivot: rp(CbB0.x, CbB0.y),
    },
    points: {
      CbB0: bodyB0,
      CbB1: bodyB1,
      CbT0: rp(PTrace.x, PTrace.y),
      CbF0,
      CbF1,
      CbE0,
      CbE1,
    },
    layoutPoints,
    layoutBottomStartCtrl,
    layoutBottomEndCtrl,
    layoutTopStartCtrl: layoutStartCtrl,
    layoutTopEndCtrl: layoutEndCtrl,
    bottomCurve: {
      start: bodyB0,
      end: CbF0,
      startCtrl: bottomStartCtrl,
      endCtrl: bottomEndCtrl,
      startCtrlBase: bottom.startCtrlBase,
      endCtrlBase: bottom.endCtrlBase,
    },
    topCurve: {
      start: bodyB1,
      end: CbE1,
      startCtrl: topStartCtrl,
      endCtrl: topEndCtrl,
      startCtrlBase: topStartCtrlBase,
      endCtrlBase: topEndCtrlBase,
    },
    draftPoints: [
      bodyB0,
      bodyB1,
      CbF0,
      CbF1,
      CbE0,
      CbE1,
      ...Object.values(layoutPoints),
      rp(PTrace.x, PTrace.y),
      bottomStartCtrl,
      bottomEndCtrl,
      topStartCtrl,
      topEndCtrl,
      layoutBottomStartCtrl,
    ],
    metrics: {
      collarBandNeckLength: Number(neckLength.toFixed(4)),
      collarBandTargetHalfNeck: Number(targetHalfNeck.toFixed(4)),
      collarBandLengthError: Number((neckLength - targetHalfNeck).toFixed(4)),
      collarBandFrontTraceLength: Number(walkedTarget.toFixed(4)),
      collarBandFrontTracePercent: Number((tracePercent * 100).toFixed(3)),
      collarBandBackHeight: Number(backHeight.toFixed(4)),
      collarBandFrontHeight: Number(frontHeight.toFixed(4)),
      collarBandExtensionWidth: Number(dist(CbF0, CbE0).toFixed(4)),
    },
    frontNeckToTrace: sampleQuadratic(split.left.p0, split.left.p1, split.left.p2, 20),
    buttons: [bodyHardware.button, layoutHardware.button],
    buttonholes: [bodyHardware.hole, layoutHardware.hole],
  };
}

const LAYOUT_B0_LIFT = 0.375;
const TARGET_BACK_ANGLE = (89 * Math.PI) / 180;
const TARGET_FRONT_ANGLE = (127.5 * Math.PI) / 180;

const rotateVec = (vector, angle) => ({
  x: (vector.x * Math.cos(angle)) - (vector.y * Math.sin(angle)),
  y: (vector.x * Math.sin(angle)) + (vector.y * Math.cos(angle)),
});

const pickToward = (axis, toward, angle) => {
  const unit = normalize(axis);
  const goal = normalize(toward);
  const left = rotateVec(unit, angle);
  const right = rotateVec(unit, -angle);
  return ((left.x * goal.x) + (left.y * goal.y)) >= ((right.x * goal.x) + (right.y * goal.y))
    ? left
    : right;
};

const neckFollowError = (p0, p1, p2, p3, samples = []) => (
  samples.reduce((sum, point) => sum + closestCubicDistance(p0, p1, p2, p3, point, 28), 0)
);

const toSixteenth = (value) => Math.round(n(value, 0) / 0.0625) * 0.0625;

const snapCtrl = (point, base) => ({
  x: (Number(base?.x) || 0) + toSixteenth(point.x - (Number(base?.x) || 0)),
  y: (Number(base?.y) || 0) + toSixteenth(point.y - (Number(base?.y) || 0)),
});

const offsetGrid = (min, max, step) => {
  const values = [];
  for (let value = min; value <= max + 1e-9; value += step) values.push(toSixteenth(value));
  return values;
};

const solveCollarBandHandles = (collar) => {
  const B0 = collar.points.CbB0;
  const B1 = collar.points.CbB1;
  const F0 = collar.points.CbF0;
  const T0 = collar.points.CbT0;
  const E0 = collar.points.CbE0;
  const E1 = collar.points.CbE1;
  const neckSamples = Array.isArray(collar.frontNeckToTrace) ? collar.frontNeckToTrace : [F0, T0];
  const chord = Math.max(0.4, dist(B0, F0));
  const topChord = Math.max(0.4, dist(B1, E1));
  const neckDir = pickToward({ x: B1.x - B0.x, y: B1.y - B0.y }, { x: F0.x - B0.x, y: F0.y - B0.y }, TARGET_BACK_ANGLE);
  const startBase = collar.bottomCurve.startCtrlBase;
  const endBase = collar.bottomCurve.endCtrlBase;
  const startLens = [0.2, 0.26, 0.32, 0.38, 0.46].map((factor) => factor * chord);
  const endXs = offsetGrid(-0.75, 0.75, 0.125);
  const endYs = offsetGrid(-1.125, 0.25, 0.125);
  let best = {
    startCtrl: snapCtrl({ x: startBase.x, y: startBase.y }, startBase),
    endCtrl: snapCtrl({ x: endBase.x, y: endBase.y - 0.5 }, endBase),
    error: Infinity,
  };

  startLens.forEach((startLen) => {
    const startCtrl = snapCtrl({
      x: B0.x + (neckDir.x * startLen),
      y: B0.y + (neckDir.y * startLen),
    }, startBase);
    endXs.forEach((dx) => {
      endYs.forEach((dy) => {
        const endCtrl = { x: endBase.x + dx, y: endBase.y + dy };
        const error = (
          (closestCubicDistance(B0, startCtrl, endCtrl, F0, T0, 40) * 36)
          + neckFollowError(B0, startCtrl, endCtrl, F0, neckSamples)
          + ((dx * dx) + (dy * dy)) * 0.35
        );
        if (error < best.error) best = { startCtrl, endCtrl, error };
      });
    });
  });

  const refineXs = offsetGrid(best.endCtrl.x - endBase.x - 0.125, best.endCtrl.x - endBase.x + 0.125, 0.0625);
  const refineYs = offsetGrid(best.endCtrl.y - endBase.y - 0.125, best.endCtrl.y - endBase.y + 0.125, 0.0625);
  refineXs.forEach((dx) => {
    refineYs.forEach((dy) => {
      const endCtrl = { x: endBase.x + dx, y: endBase.y + dy };
      const error = (
        (closestCubicDistance(B0, best.startCtrl, endCtrl, F0, T0, 48) * 36)
        + neckFollowError(B0, best.startCtrl, endCtrl, F0, neckSamples)
        + ((dx * dx) + (dy * dy)) * 0.35
      );
      if (error < best.error) best = { ...best, endCtrl, error };
    });
  });

  const topOut = pickToward({ x: B0.x - B1.x, y: B0.y - B1.y }, { x: E1.x - B1.x, y: E1.y - B1.y }, TARGET_BACK_ANGLE);
  const topIn = pickToward({ x: E0.x - E1.x, y: E0.y - E1.y }, { x: B1.x - E1.x, y: B1.y - E1.y }, TARGET_FRONT_ANGLE);
  const topStartLen = clamp(dist(B1, collar.topCurve.startCtrl) || (topChord / 3), 0.2 * topChord, 0.55 * topChord);
  const topEndLen = clamp(dist(E1, collar.topCurve.endCtrl) || (topChord / 3), 0.2 * topChord, 0.55 * topChord);
  const topStartCtrl = rp(B1.x + (topOut.x * topStartLen), B1.y + (topOut.y * topStartLen));
  const topEndCtrl = rp(E1.x + (topIn.x * topEndLen), E1.y + (topIn.y * topEndLen));

  return {
    bottomStartCtrl: best.startCtrl,
    bottomEndCtrl: best.endCtrl,
    topStartCtrl,
    topEndCtrl,
  };
};

export function recomputeShirtCollarBandGeometry(buildArgs = {}, options = {}) {
  const currentB0Dy = n(buildArgs.layoutB0Dy, 0);
  const currentB1Dy = n(buildArgs.layoutB1Dy, 0);
  const preserveBackPoints = options.preserveBackPoints === true
    || hasManualDelta(currentB0Dy, currentB1Dy)
    || hasManualDelta(n(buildArgs.bandBackManual, 0));

  const seed = buildShirtCollarBand({
    ...buildArgs,
    topStartCtrlDx: 0,
    topStartCtrlDy: 0,
    topEndCtrlDx: 0,
    topEndCtrlDy: 0,
    topFollowBottom: true,
    topStartAbsX: 0,
    topStartAbsY: 0,
    topEndAbsX: 0,
    topEndAbsY: 0,
    bottomStartCtrlDx: 0,
    bottomStartCtrlDy: 0,
    bottomEndCtrlDx: 0,
    bottomEndCtrlDy: 0,
    neckCtrlDx: 0,
    neckCtrlDy: 0,
    layoutB0Dy: preserveBackPoints ? currentB0Dy : 0,
    layoutB1Dy: preserveBackPoints ? currentB1Dy : 0,
  });
  if (!seed) return null;

  const backHeight = Math.max(0.25, n(buildArgs.bandBackHeight, 1.25));
  const mappedB0 = seed.layoutPoints.CbB0;
  const mappedT0 = seed.layoutPoints.CbT0;
  const layoutB0Dy = preserveBackPoints
    ? currentB0Dy
    : (mappedT0.y - LAYOUT_B0_LIFT) - mappedB0.y;
  const layoutB1Dy = 0;

  const placed = buildShirtCollarBand({
    ...buildArgs,
    topStartCtrlDx: 0,
    topStartCtrlDy: 0,
    topEndCtrlDx: 0,
    topEndCtrlDy: 0,
    topFollowBottom: true,
    topStartAbsX: 0,
    topStartAbsY: 0,
    topEndAbsX: 0,
    topEndAbsY: 0,
    bottomStartCtrlDx: 0,
    bottomStartCtrlDy: 0,
    bottomEndCtrlDx: 0,
    bottomEndCtrlDy: 0,
    neckCtrlDx: 0,
    neckCtrlDy: 0,
    layoutB0Dy,
    layoutB1Dy,
  });
  if (!placed) return null;

  const solved = solveCollarBandHandles(placed);
  const bottomBase = placed.bottomCurve;
  const topBase = placed.topCurve;

  return {
    pointDeltas: {
      CbB0: { x: 0, y: toSixteenth(-layoutB0Dy) },
      CbB1: { x: 0, y: toSixteenth(-layoutB1Dy) },
    },
    measurements: {
      collarBandBottomStartCtrlDx: toSixteenth(solved.bottomStartCtrl.x - bottomBase.startCtrlBase.x),
      collarBandBottomStartCtrlDy: toSixteenth(solved.bottomStartCtrl.y - bottomBase.startCtrlBase.y),
      collarBandBottomEndCtrlDx: toSixteenth(solved.bottomEndCtrl.x - bottomBase.endCtrlBase.x),
      collarBandBottomEndCtrlDy: toSixteenth(solved.bottomEndCtrl.y - bottomBase.endCtrlBase.y),
      collarBandNeckCtrlDx: 0,
      collarBandNeckCtrlDy: 0,
      collarBandTopStartCtrlDx: 0,
      collarBandTopStartCtrlDy: 0,
      collarBandTopEndCtrlDx: 0,
      collarBandTopEndCtrlDy: -1,
      collarBandTopFollowBottom: 1,
      collarBandTopStartAbsX: 0,
      collarBandTopStartAbsY: 0,
      collarBandTopEndAbsX: 0,
      collarBandTopEndAbsY: 0,
    },
  };
}
